import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input, Select,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { callCenterOutcomesApi, callCenterQueuesApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const CallCenterOutcomes = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [queues, setQueues] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [resOutcomes, resQueues] = await Promise.all([
        callCenterOutcomesApi.getAll(),
        callCenterQueuesApi.getAll(),
      ]);
      const payload = resOutcomes?.data ?? [];
      setRows(Array.isArray(payload) ? payload : payload.data ?? []);
      
      const qPayload = resQueues?.data ?? [];
      setQueues(Array.isArray(qPayload) ? qPayload : qPayload.data ?? []);
    } catch (error) {
      console.error('خطا در دریافت نتایج تماس:', error);
      message.error(error?.message || 'خطا در دریافت نتایج تماس');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openAddModal = () => {
    setAddOpen(true);
    addForm.resetFields();
  };

  const submitAdd = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      await callCenterOutcomesApi.create({
        queue_id: values.queue_id,
        name: values.name,
      });
      message.success('نتیجه تماس با موفقیت اضافه شد');
      setAddOpen(false);
      await fetchRows();
    } catch (error) {
      if (error?.errorFields) return;
      console.error('خطا در افزودن نتیجه تماس:', error);
      if (error instanceof ValidationError) {
        show422Error(error, addForm);
      } else {
        message.error(error?.message || 'خطا در افزودن نتیجه تماس');
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const deleteRow = async (row) => {
    setDeleteLoadingId(row.id);
    try {
      await callCenterOutcomesApi.delete(row.id);
      message.success('نتیجه تماس حذف شد');
      await fetchRows();
    } catch (error) {
      console.error('خطا در حذف نتیجه تماس:', error);
      message.error(error?.message || 'خطا در حذف نتیجه تماس');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'نام نتیجه (Outcomes)',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'صف (Queue)',
      key: 'queue',
      render: (_, record) => record.queue ? `${record.queue.title} (${record.queue.name})` : '-',
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <PermissionGate module="callcenter" permission="manage_callcenter_outcomes">
            <Popconfirm
              title="آیا از حذف این نتیجه مطمئن هستید؟"
              onConfirm={() => deleteRow(record)}
              okText="بله"
              cancelText="خیر"
              placement="topRight"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoadingId === record.id}
                disabled={deleteLoadingId && deleteLoadingId !== record.id}
              />
            </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout
      title="مدیریت نتایج تماس (Outcomes)"
      activeMenu="/call-center/outcomes"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'نتایج تماس' },
      ]}
    >
      <div className="w-full">
        <PermissionGate module="callcenter" permission="manage_callcenter_outcomes" showWarning>
          <Card
            bordered={false}
            className="shadow-sm rounded-lg"
            styles={{ body: { padding: '1.5rem' } }}
            title={
              <div className="flex items-center gap-2">
                <TagsOutlined className="text-gray-500" />
                <span className="font-semibold text-gray-800">نتایج تماس مجاز برای صف‌ها</span>
              </div>
            }
            extra={
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => fetchRows()}>
                  به‌روزرسانی
                </Button>
                <Button
                  type="primary"
                  className={PANEL_PRIMARY_BTN_CLASS}
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                >
                  افزودن نتیجه
                </Button>
              </Space>
            }
          >
            {loading ? (
              <TableSkeleton columns={3} rows={5} />
            ) : (
              <Table
                columns={columns}
                dataSource={rows}
                rowKey="id"
                pagination={false}
                size="middle"
              />
            )}
          </Card>
        </PermissionGate>
      </div>

      <Modal
        title="افزودن نتیجه تماس جدید"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        confirmLoading={addSubmitting}
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={addForm} layout="vertical" className="mt-4">
          <Form.Item
            name="queue_id"
            label="صف مربوطه (Queue)"
            rules={[{ required: true, message: 'لطفاً صف را انتخاب کنید' }]}
            help="صفی که این نتیجه برای آن مجاز است."
          >
            <Select placeholder="انتخاب صف...">
              {queues.map((q) => (
                <Select.Option key={q.id} value={q.id}>
                  {q.title} ({q.name})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="عنوان نتیجه"
            rules={[{ required: true, message: 'لطفاً عنوان نتیجه را وارد کنید' }]}
            help="مثال: فروش انجام شد، نیاز به پیگیری و..."
          >
            <Input placeholder="فروش انجام شد" />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CallCenterOutcomes;
